<template>
  <p class="body-3-bold tool-desc text-center text-base pb-4">
    {{ $t("calculator-suite.masterDesc") }}
  </p>
  <div class="calculator-params-results">
    <section class="params-card">
      <h2 class="body-3-bold">{{ $t("calculator-suite.setParams") }}</h2>
      <div class="inputs">
        <InputSearch
          id="lot-size-options"
          v-model="market"
          name="lot-size-dropdown"
          :label="$t('calculator-suite.market')"
          :placeholder="$t('calculator-suite.marketPlaceholder')"
          :options="
            options.map((i) => ({
              text: $t(i.text),
              value: i.value,
            }))
          "
        />

        <div
          v-for="(input, index) in filteredParams"
          :key="`${market}-${input.key}`"
          :class="{
            'use-both-cols': shouldUseBothCols(index),
          }"
        >
          <TVInputNumber
            v-if="input.type === 'number'"
            v-model="input.value"
            :label="$t(input.text)"
            :placeholder="$t(input.placeholder)"
            :name="input.text"
            :tooltip-message="$t(input.tooltip)"
            tooltip-dir-desk="right"
            tooltip-dir-mobile="top"
            :tooltip-max-width="246"
          />
          <InputSearch
            v-else-if="input.options"
            :id="`select-${input.key}`"
            v-model="input.value"
            :name="input.text"
            :label="$t(input.text)"
            :placeholder="$t(input.placeholder)"
            :model-value="input.value"
            :options="input.options"
          />
          <InputCheckbox
            v-else-if="input.isCheckbox"
            v-model="input.value"
            :name="input.text"
            :label="$t(input.text)"
            class="flex align-items-center checkbox-bg mb-3 lg:mb-0 mt-0 lg:mt-5"
          />
        </div>
      </div>
      <button
        class="primary-button-medium"
        @click="calculate"
        :disabled="!isFormValid"
      >
        {{ $t("calculator-suite.calculate") }}
      </button>
      <p class="disclaimer">{{ $t("calculator-suite.masterFooter") }}</p>
    </section>

    <section class="results-card w-full flex flex-column">
      <ResultsSkeleton v-if="isLoading" class="p-3" />

      <div
        v-else
        class="result-info mb-4"
        style="background-color: transparent"
      >
        <h2 class="body-3-bold">{{ $t("calculator-suite.results") }}:</h2>
        <div
          v-for="(result, index) in results"
          :key="index"
          class="item p-3"
          style="background-color: #ecf1f7"
        >
          <h4 class="body-4 flex gap-2 align-items-center">
            {{ $t(result.text) }}:
          </h4>
          <span class="body-4-bold" v-if="result.value != null">
            {{ formatValue(result.value, result.currency) }}
          </span>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, defineEmits, onMounted, watch } from "vue";
import TVInputNumber from "../InputNumber.vue";
import InputSearch from "../InputSearch.vue";
import InputCheckbox from "../InputCheckbox.vue";
import ResultsSkeleton from "./ResultsSkeleton.vue";
import { getAssets, masterCalculate } from "./utils/calculateRequest";
import { useI18n } from "vue-i18n";
import {
  leverageMT45XLEV,
  optionsCurrencyForex,
} from "./utils/dropdownOptions";

const { t } = useI18n();
const market = ref("");
const isLoading = ref(false);

const options = [
  { text: "calculator-suite.forex", value: "masterforex" },
  { text: "calculator-suite.cfd", value: "mastercfds" },
  { text: "calculator-suite.stocks", value: "masterstocks" },
  /* { text: "calculator-suite.futures", value: "masterfutures" }, */
];

const masterParams = ref([
  {
    text: "calculator-suite.asset",
    placeholder: "calculator-suite.assetPlaceholder",
    options: [],
    key: "asset",
    value: null,
  },
  {
    text: "calculator-suite.accountTypeH",
    placeholder: "calculator-suite.selectAnOption",
    options: [
      { text: "ILC", value: "iLC" },
      { text: "XLev", value: "XLev" },
    ],
    key: "account_type",
    value: null,
  },
  {
    text: "calculator-suite.leverage",
    placeholder: "calculator-suite.selectAnOption",
    options: leverageMT45XLEV.map((i) => ({
      text: i.name,
      value: i.code,
    })),
    key: "leverage",
    value: null,
  },
  {
    text: "calculator-suite.accountCurrency",
    placeholder: "calculator-suite.selectAnOption",
    options: optionsCurrencyForex,
    key: "account_currency",
    value: null,
  },
  {
    text: "calculator-suite.lotsizee",
    placeholder: "calculator-suite.enterLotsize",
    key: "volume",
    value: null,
    tooltip: t("calculator-suite.lotsizeTooltip"),
    type: "number",
  },
  {
    text: "calculator-suite.calculateMP",
    placeholder: "",
    isCheckbox: true,
    key: "calculateMP",
    value: true,
  },
  {
    text: "calculator-suite.side",
    placeholder: "calculator-suite.selectAnOption",
    options: [
      { text: t("spreadsTable.buy"), value: "BUY" },
      { text: t("spreadsTable.sell"), value: "SELL" },
    ],
    key: "side",
    value: null,
  },
  {
    text: "calculator-suite.openprice",
    placeholder: "calculator-suite.enterAValue",
    key: "open_price",
    value: null,
    tooltip: "",
    type: "number",
  },
  {
    text: "calculator-suite.closeprice",
    placeholder: "calculator-suite.enterAValue",
    key: "close_price",
    value: null,
    tooltip: "",
    type: "number",
  },
]);

const masterResults = ref([
  {
    text: "calculator-suite.margin",
    currency: "USD",
    key: "margin",
    tooltip: t("calculator-suite.marginTooltip"),
    value: null,
  },
  {
    text: "calculator-suite.spread",
    currency: "",
    key: "spread",
    tooltip: t("calculator-suite.spreadTooltip"),
    value: null,
  },
  {
    text: "calculator-suite.comission",
    currency: "USD",
    key: "commission",
    tooltip: t("calculator-suite.comissionTooltip"),
    value: null,
  },
  {
    text: "calculator-suite.profit",
    currency: "USD",
    key: "profit",
    tooltip: t("calculator-suite.profitTooltip"),
    value: null,
  },
  {
    text: "calculator-suite.shortSwap",
    currency: "USD",
    key: "short_swap",
    tooltip: t("calculator-suite.shortSwapTooltip"),
    value: null,
  },
  {
    text: "calculator-suite.longSwap",
    currency: "USD",
    key: "long_swap",
    tooltip: t("calculator-suite.longSwapTooltip"),
    value: null,
  },
  {
    text: "calculator-suite.pipVal",
    currency: "USD",
    key: "pip",
    tooltip: t("calculator-suite.pipValTooltip"),
    value: null,
  },
]);

const assets = ref(null);
const params = ref(masterParams.value);
const results = ref(masterResults.value);

watch(
  () => market.value,
  () => {
    params.value.forEach((param) => {
      if (param.key === "calculateMP") {
        param.value = true;
      } else {
        param.value = null;
      }
    });
    results.value.forEach((result) => {
      result.value = null;
    });
  }
);

onMounted(async () => {
  try {
    isLoading.value = true;
    const localAssets = JSON.parse(localStorage.getItem("assets"));
    assets.value = localAssets ?? (await getAssets());
    isLoading.value = false;
  } catch (error) {
    console.log(error);
    isLoading.value = false;
  }
});

const isCalculateMPEnabled = computed(() => {
  const param = params.value.find((p) => p.key === "calculateMP");
  return !!param?.value;
});

const filteredParams = computed(() => {
  if (!market.value) return [];

  const excludeKeys = [];

  if (market.value.includes("mastercfds")) {
    excludeKeys.push("leverage");
  }
  if (market.value.includes("masterfutures")) {
    excludeKeys.push("leverage", "account_type", "account_currency");
  }
  if (market.value.includes("masterstocks")) {
    excludeKeys.push("account_currency", "account_type");
  }

  if (!isCalculateMPEnabled.value) {
    excludeKeys.push("side", "open_price", "close_price");
  }

  const newParams = params.value.filter(
    (param) => !excludeKeys.includes(param.key)
  );

  const assetParam = newParams.find((p) => p.key === "asset");
  const cleanedMarket =
    market.value === "mastercfds" ? "cfd" : market.value.replace(/^master/, "");

  if (assetParam && assets.value) {
    assetParam.options = assets.value[cleanedMarket].map((i) => ({
      text: i,
      value: i,
    }));
  }

  return newParams;
});

const isFormValid = computed(() => {
  if (!market.value) return false;

  const visibleParams = filteredParams.value.filter((p) => !p.isCheckbox);

  return visibleParams.every(
    (param) => param.value !== null && param.value !== ""
  );
});

const calculate = async () => {
  try {
    isLoading.value = true;

    const paramsData = params.value.reduce((acc, { key, value }) => {
      if (value !== "" && value !== null) {
        acc[key] = value;
      }
      return acc;
    }, {});
    const response = await masterCalculate(market.value, paramsData);

    if (!response) {
      isLoading.value = false;
      return;
    }

    results.value = Object.entries(response)
      .filter(([key, value]) => key !== "status" && value != null)
      .map(([key, value]) => {
        const existing = masterResults.value.find((r) => r.key === key);
        return {
          key,
          text: existing?.text || key,
          value,
          currency: existing?.currency,
          tooltip: existing?.tooltip || "",
        };
      });

    isLoading.value = false;
  } catch (error) {
    console.log(error);
    isLoading.value = false;
  }
};

const formatValue = (value, currency) => {
  const number = Number(value);
  if (isNaN(number)) return value;
  const formattedValue = number.toLocaleString("es-CO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${formattedValue} ${currency ? t(currency) : ""}`;
};

const shouldUseBothCols = (index) => {
  const paramsAfterMarket = filteredParams.value.length;
  return paramsAfterMarket % 2 !== 0 && index === paramsAfterMarket - 1;
};
</script>
